import { createApiClient, createAssetUrl } from '@gftg/api-client';

const baseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';

export const api = createApiClient({ baseUrl });

/** Turns an asset id from any response into something an `<img>` can load. */
export const assetUrl = createAssetUrl({ baseUrl });

/** Carries the status so a retry can tell "the venue is down" from "there is no such session". */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** The API's own explanation, when it sent one worth showing to the attendee. */
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * A session that ended mid-scroll is not an error any screen can do anything about, so it is
 * announced once and the shell answers by showing the sign-in screen again. Registered by
 * `SessionProvider`; kept here because this is the only place that sees every response.
 */
type UnauthorizedListener = () => void;
let onUnauthorized: UnauthorizedListener | undefined;

export function setUnauthorizedListener(listener: UnauthorizedListener | undefined): void {
  onUnauthorized = listener;
}

/** Nest's error body. `message` is a string for thrown exceptions, an array for validation. */
type ErrorBody = { message?: string | string[] };

/** Paths that report 401 without meaning "drop the session this browser already holds". */
function isAnonymousAuthPath(url: string): boolean {
  try {
    const path = new URL(url).pathname;
    return (
      path.endsWith('/auth/me') ||
      path.endsWith('/auth/consume') ||
      path.endsWith('/auth/request-link')
    );
  } catch {
    return false;
  }
}

function readDetail(body: unknown): string | undefined {
  const message = (body as ErrorBody | undefined)?.message;
  if (Array.isArray(message)) {
    return message.join('. ');
  }
  return typeof message === 'string' ? message : undefined;
}

/**
 * Every screen wants the same two things from a request: the body, or one sentence it can show the
 * attendee. This is that, in one place, so a new query cannot forget to check `error` and hand a
 * screen an undefined body it will read as "empty" (DATA-06).
 */
export async function unwrap<T>(
  message: string,
  request: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await request;

  if (response.status === 401) {
    // `/auth/me` and `/auth/consume` answer 401 as a normal outcome (signed out, spent link).
    // Treating those as "the session just died" wipes a login that just succeeded — which is
    // exactly what React StrictMode does when it spends the one-use link twice.
    if (!isAnonymousAuthPath(response.url)) {
      onUnauthorized?.();
    }
    throw new ApiError('Your session has ended. Sign in again.', 401, readDetail(error));
  }

  if (!response.ok || data === undefined) {
    throw new ApiError(message, response.status, readDetail(error));
  }

  return data;
}

/**
 * Signing out answers 204, so `data === undefined` is the success case rather than the failure one
 * — which is why it cannot go through `unwrap`.
 */
export async function unwrapEmpty(
  message: string,
  request: Promise<{ error?: unknown; response: Response }>,
): Promise<void> {
  const { error, response } = await request;

  if (!response.ok) {
    throw new ApiError(message, response.status, readDetail(error));
  }
}

/**
 * What an `ApiError` should read as on screen. The API's own sentence wins where it wrote one —
 * "your registration is no longer active, email …" (ACC-06) is the whole answer, and a generic
 * fallback in front of it would be worse than useless.
 */
export function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.detail ?? error.message;
  }
  return fallback;
}
